import * as SliderPrimitive from '@radix-ui/react-slider'
import { forwardRef } from 'react'
import { cn } from './cn'

export type SliderProps = {
  /** Current value(s). Single-thumb passes `[n]`; range passes `[min, max]`. */
  value: number[]
  onChange: (value: number[]) => void
  /** Inclusive minimum. Default 0. */
  min?: number
  /** Inclusive maximum. Default 100. */
  max?: number
  /** Step granularity. Default 1. */
  step?: number
  /** Disabled state — track + thumb dim, no interaction. */
  disabled?: boolean
  /** Aria label for screen readers. Required when there is no visible label. */
  ariaLabel?: string
  /** Extra className applied to the root. */
  className?: string
}

/**
 * Single- or multi-thumb slider — Radix-backed, theme-tokenised.
 *
 * @example Single value
 *   const [val, setVal] = useState([50])
 *   <Slider value={val} onChange={setVal} ariaLabel="Volumen" />
 *
 * @example Range
 *   const [range, setRange] = useState([20, 80])
 *   <Slider value={range} onChange={setRange} ariaLabel="Rango horario" />
 */
export const Slider = forwardRef<HTMLSpanElement, SliderProps>(function Slider(
  { value, onChange, min = 0, max = 100, step = 1, disabled, ariaLabel, className },
  ref,
) {
  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn('slider', disabled && 'slider--disabled', className)}
      value={value}
      min={min}
      max={max}
      step={step}
      {...(disabled === true ? { disabled: true } : {})}
      onValueChange={onChange}
      aria-label={ariaLabel}
    >
      <SliderPrimitive.Track className="slider-track">
        <SliderPrimitive.Range className="slider-range" />
      </SliderPrimitive.Track>
      {value.map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          className="slider-thumb"
          aria-label={ariaLabel ? `${ariaLabel} — pulgar ${i + 1}` : undefined}
        />
      ))}
    </SliderPrimitive.Root>
  )
})
